import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { isEmbeddedDiscovery, resolveEmbeddedFlowProps } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { Sel, STag } from '#~/stag/tag.js';
import { isRtsp } from '@studio/models/video/rtsp';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { useProducer, useStore } from '../store/hooks.js';
import { R9_PREVIEW_COPY } from '../store/preview-outcome.js';
import { Panel } from '../store/types.js';
import { isRtspReadyOverrideEnabled } from './ready-override.js';

/** Video `<Footer>` component. */
export const Footer = (props: _Footer.Props): ReactElement => {
  const producer = useProducer();
  const sourceSelection = useStore((µ) => µ.source.selection);
  const roomId = useStore((µ) => µ.roomId);
  const manufacturerConfig = useStore((µ) => µ.manufacturer.config);
  // Drive the conditional next-step routing (see handleNext).
  const protocol = useStore((µ) => µ.candidate.video?.protocol);
  const modelId = useStore((µ) => µ.candidate.modelId);
  // B2 (ENG-4159): connectivity signal for the RTSP-assist Continue gate.
  // Written by panel.tsx (:31-35) — true ONLY when the preview device reports
  // StatusStreamState.STREAM_STATE_PLAYING (a real, playing stream).
  // The e2e ready seam is read here as well as in panel.tsx: the Panel raises
  // `isReady` only when its computed value changes, so a reset that lands after
  // the seam is set (a brand/model template resolving mid URL edit) left
  // Continue disabled for the rest of the spec.
  const isReady = useStore((µ) => µ.video.isReady || isRtspReadyOverrideEnabled());

  // ENG-3796. `isReady` here means the bridge is actually PLAYING this stream
  // (video/panel.tsx gates on STREAM_STATE_PLAYING), which is only true once
  // Check Connectivity has run — that button is what commits the typed URL to
  // `candidate.video`; typing alone resets it to a bare `{ protocol }`.
  //
  // Production can advance without it: the preview is visibly empty and two
  // more steps follow. The funnel commits straight into its connection test, so
  // an ungated Continue produced a camera with NO address, and the user learned
  // about it three screens later as "We couldn't reach that camera" (reported
  // live 2026-08-28). Undefined for every other caller, so the normal Add Video
  // Source flow is unchanged.
  const onboarding = useStore((µ) => µ.onboarding);
  const blocked = onboarding !== undefined && !isReady;

  // A transient Iris service failure (R9) means this step cannot complete right now; advancing would
  // carry the operator onto an unfinished step. Gate Continue additively — separate from the
  // onboarding preview gate above, and never touching it — until the step recovers (the copy clears).
  const previewAvailability = useStore((µ) => µ.previewAvailability);
  const previewCopy = useStore((µ) => µ.previewCopy);
  const serviceFailure = previewAvailability === 'unavailable' && previewCopy === R9_PREVIEW_COPY;
  // No video configuration at all: content.tsx shows its NoVideoFallback ("Enter
  // this camera's RTSP stream") instead of an editor. Continue would otherwise
  // route to Manufacturer → Control and commit a camera with no video, so the
  // fallback's link is the only way forward. Keyed on `protocol === undefined`,
  // not "no editor", so every path that carries a video protocol keeps its
  // existing behavior.
  const hasNoVideo = protocol === undefined;

  const { track } = useAnalytics();

  const handleBack = useLatestFn(() => {
    producer((draft) => {
      draft.panel--;
    });
  });

  const handleNext = useLatestFn(() => {
    if (sourceSelection) {
      const model = manufacturerConfig?.model;
      const deviceAnalyticsCatalogContext = resolveDeviceAnalyticsCatalogContextFromModel(model);

      // Per ENG-3960 §1.7 — additively carry is_embedded + linking_surface on the
      // embedded flow's reuse of this shared step; non-embedded path is unchanged.
      track(EVENT_NAMES.DEVICE_CONTINUE_FROM_VIDEO, EVENT_CATEGORIES.DEVICE, {
        deviceId: sourceSelection.id,
        studio_id: roomId,
        ...deviceAnalyticsCatalogContext,
        ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection))
      });
    }

    producer((draft) => {
      // RTSP-Assist flows (RTSP / ONVIF / manual RTSP — isRtsp) collect
      // brand/model on this video screen, and any source already carrying a
      // modelId is fully resolved; both skip straight to Control. A non-RTSP
      // source WITHOUT a modelId (e.g. NDI) still needs the classic driver +
      // nickname screen (ENG-4159 regression), so route through Manufacturer.
      draft.panel = !modelId && !isRtsp(protocol) ? Panel.Manufacturer : Panel.Control;
    });
  });

  return (
    <_Footer.Root {...props}>
      <_Footer.Back onClick={handleBack} />
      <div className='flex items-center gap-16'>
        {serviceFailure && (
          <span className='text-sm text-muted'>
            Continue becomes available once this step completes.
          </span>
        )}
        <_Footer.Next
          disabled={(isRtsp(protocol) && !isReady) || blocked || serviceFailure || hasNoVideo}
          {...(blocked ? { label: 'Check connectivity first' } : {})}
          onClick={handleNext}
          {...STag(Sel.ConfigVideoDeviceVideo, 'footer.continue')}
        />
      </div>
    </_Footer.Root>
  );
};
